// Pure, framework-free helpers for the M17 eBay connect flow (#293, Plan 2
// Slice 4, .pen R2CDd "eBay Connect — Flow"). Kept out of the React components
// so they can be unit tested in the project's `node` Vitest environment (no
// jsdom / testing-library).


/**
 * The four builtin `SellerChannel` keys whose management toggle rows are no
 * longer shown on Settings → Channels (Plan 3, Slice A). The backend still
 * seeds/enables these rows so the listings tracker is unaffected — this set only
 * drives which rows the Settings UI renders. `own` (the storefront channel) is
 * intentionally NOT in this set: it is managed by the Storefront card/page.
 * Mirrors `BUILTIN_META`'s non-`own` keys in Channels.jsx (single source here).
 */
const HIDDEN_BUILTIN_CHANNEL_KEYS = ['ebay', 'facebook', 'craigslist', 'carpart'];


/**
 * Whether a channel `key` is one of the four builtin keys whose toggle row is
 * hidden from Settings → Channels. Defensive: non-string / missing → false.
 */
function isBuiltinChannel(key) {
  return HIDDEN_BUILTIN_CHANNEL_KEYS.includes(key);
}


/**
 * Filter the SellerChannel rows down to the ones the Settings → Channels list
 * should render: drops the four hidden builtin channels (`ebay`/`facebook`/
 * `craigslist`/`carpart`) and keeps everything else (custom channels and `own`).
 * Defensive: non-array input → `[]`; rows without a `key` are kept (they are not
 * a hidden builtin).
 */
function visibleSettingsChannels(channels) {
  if (!Array.isArray(channels)) {
    return [];
  }
  return channels.filter((channel) => !isBuiltinChannel(channel?.key));
}


/**
 * Maps the OAuth-callback `?ebay=<status>` return param to a toast. The browser
 * lands back on /app/settings/channels with one of these statuses after the eBay
 * consent round-trip (R2CDd post-redirect states). `variant` is the semantic
 * intent ('success' | 'danger'); the page hands it to `push()`.
 */
const EBAY_TOAST = {
  connected: { message: 'eBay account connected', variant: 'success' },
  error: { message: 'Couldn\'t connect to eBay', variant: 'danger' },
  badstate: { message: 'Connect link expired — try again', variant: 'danger' },
  limit: { message: 'Account limit reached — upgrade your plan', variant: 'danger' },
};


/**
 * Resolve the return toast for a `?ebay=` status. Returns `{ message, variant }`
 * for a known status or `null` for unknown / undefined (so the page shows nothing).
 */
function ebayReturnToast(status) {
  return EBAY_TOAST[status] || null;
}


/**
 * The "N of M accounts" count label for the eBay section header (R2CDd). `cap`
 * `null` reads as unlimited (∞). Returns `null` when no limits row is available
 * yet so the header can simply omit the label while loading.
 */
function ebayAccountLabel(row) {
  if (!row) {
    return null;
  }
  const count = row.count ?? 0;
  if (row.cap == null) {
    return `${count} of ∞ accounts`;
  }
  return `${count} of ${row.cap} accounts`;
}


/**
 * Find the limits row for a connection `type` in the `[{ type, cap, count,
 * can_add }]` array. Returns the row or `null` when absent / not loaded.
 */
function ebayLimitsFor(limits, type = 'ebay') {
  if (!Array.isArray(limits)) {
    return null;
  }
  return limits.find((row) => row?.type === type) || null;
}


/**
 * Whether the Add-eBay-account affordance should be active for a limits row.
 * Fail-closed: `null` (limits not loaded) → false; the component gates the
 * loading flash separately via the query's pending state (R2CDd).
 */
function canAddEbay(limitsRow) {
  return Boolean(limitsRow?.can_add);
}


/**
 * A connection is "live" (still syncing) when it is anything other than
 * disconnected. Disconnecting clears `is_default`, so a disconnected row never
 * counts toward the default or the no-default check.
 */
function isLiveConnection(connection) {
  return Boolean(connection) && connection.status !== 'disconnected';
}


/**
 * Whether the "Set as default" action should be offered for a connection
 * (R2CDd, Slice 4): only when it is connected and not already the default.
 * Expired/error/disconnected accounts can't be made default until reconnected.
 */
function canSetDefaultEbay(connection) {
  return Boolean(connection) && connection.status === 'connected' && !connection.is_default;
}


/**
 * Whether a connection needs the seller to reconnect (M17 #296): true when the
 * stored OAuth token has lapsed (`expired`) or the last sync errored (`error`).
 * Drives the card's "Reconnect" action and the listings-row advisory chip. A
 * `connected` or `disconnected` account does not need reconnect (the latter is a
 * deliberate teardown, surfaced separately). Defensive: missing → false.
 */
function needsReconnect(status) {
  return status === 'expired' || status === 'error';
}


/**
 * Whether to show the no-default advisory banner (R2CDd, Slice 4 / #294 forward
 * risk): true when there is at least one live (non-disconnected) eBay connection
 * but none of them is the default — e.g. the default was just disconnected.
 */
function shouldShowNoDefaultBanner(connections) {
  if (!Array.isArray(connections)) {
    return false;
  }
  const live = connections.filter(isLiveConnection);
  return live.length > 0 && !live.some((connection) => connection.is_default);
}


/**
 * Build the per-account eBay rows for the part-detail Listings tab (Plan 3,
 * Slice E). One row per CONNECTED eBay connection (`status === 'connected'`):
 * disconnected/expired/error accounts can't be listed against, so they are
 * skipped. Each row carries:
 *   - `id`    — the connection UUID (the toggle sends it as `connection_id`)
 *   - `label` — `label || external_account || 'eBay account'` (naming fallback)
 *   - `live`  — true when a connection-scoped eBay `ChannelListing` is live, i.e.
 *               a listing with `channel === 'ebay'` AND `connection === id` AND
 *               `state === 'live'`.
 *   - `url`   — the matching live listing's `url` (so the row can link to the eBay
 *               listing), or `''` when the row is not live / the listing has no url.
 * Defensive: non-array inputs → `[]`; rows without an `id` are skipped (can't be
 * toggled); a missing `listings` array reads as no live rows. The component
 * renders straight from this output so the logic is testable in node-Vitest.
 */
function ebayAccountListingRows(connections, listings) {
  if (!Array.isArray(connections)) {
    return [];
  }
  const rows = Array.isArray(listings) ? listings : [];
  return connections
    // type === 'ebay' is load-bearing: the connections endpoint also returns carrier
    // credentials (e.g. shipping_fedex), and an unlabeled one would otherwise render
    // here as a phantom "eBay account" row (AUTOORBIT-680).
    .filter(
      (connection) => connection?.type === 'ebay'
        && connection?.status === 'connected'
        && connection?.id != null,
    )
    .map((connection) => {
      const match = rows.find(
        (listing) => listing?.channel === 'ebay'
          && listing?.connection === connection.id
          && listing?.state === 'live',
      );
      return {
        id: connection.id,
        label: connection.label || connection.external_account || 'eBay account',
        live: Boolean(match),
        url: match?.url || '',
      };
    });
}


/**
 * Whether a connection's three eBay business policies are all present (Plan 3,
 * Slice C). The onboarding service persists `payment_policy_id`, `return_policy_id`
 * and `fulfillment_policy_id` onto `connection.business_policies`; this returns
 * true only when all three are non-empty. Drives the card's "Business policies
 * ready" vs "Finish eBay setup" state and the Re-run vs Finish button label.
 * Defensive: missing/garbage `business_policies` (null, array, string) → false.
 * NOTE: this is policy-readiness only — full listing readiness also needs a
 * merchant location (gated on sandbox seller registration, Slice D), so a true
 * here must NOT be presented as "ready to list".
 */
function ebayPoliciesReady(connection) {
  const policies = connection?.business_policies;
  if (!policies || typeof policies !== 'object' || Array.isArray(policies)) {
    return false;
  }
  return Boolean(
    policies.payment_policy_id
    && policies.return_policy_id
    && policies.fulfillment_policy_id,
  );
}


export {
  canAddEbay,
  canSetDefaultEbay,
  ebayAccountLabel,
  ebayAccountListingRows,
  ebayLimitsFor,
  ebayPoliciesReady,
  ebayReturnToast,
  HIDDEN_BUILTIN_CHANNEL_KEYS,
  isBuiltinChannel,
  isLiveConnection,
  needsReconnect,
  shouldShowNoDefaultBanner,
  visibleSettingsChannels,
};
